import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import * as R from 'ramda'
import qs from 'qs'
import debounce from 'lodash.debounce'
import { propOr } from 'ramda'

import styled from 'styled-components'

import {
  saveBookContentPositionsRoutine,
  saveScrollAnchorPositionRoutine
} from 'modules/books/ducks/actions'
import {
  selectScrollAnchorPositionY,
  selectBookContentsPositions,
  getBookContentsWithImages,
  getBookContentImages,
  getOrderedBookContentsList,
  getBookByOriginalId
} from 'modules/books/ducks/selectors'

import { BookContent } from 'types'
import { isNotNilOrEmpty } from 'utils/ramda'
import { BOOK_VIEWER_PARAMS } from 'utils/books'
import eventEmitter from 'providers/eventEmitter'
import events from 'modules/books/utils/events'
import LocalStorageService from 'services/LocalStorageService'
import { TABS } from 'modules/books/components/BookRightColumn'

import { LOCAL_STORAGE_KEYS } from 'utils/storage'
import useIsMobile from 'hooks/useIsMobile'

const hasNonEmptyImages = R.pipe(R.propOr([], 'images'), isNotNilOrEmpty)

const hasCurrentBookContentImagesAttached = R.pipe(R.last, hasNonEmptyImages)

const takeFirstImageId = R.pipe(
  R.last,
  R.propOr([], 'images'),
  R.head,
  R.propOr('', 'id')
)

const findNearestImage = R.pipe(
  // @ts-ignore
  R.reverse,
  R.find(hasNonEmptyImages),
  R.propOr([], 'images'),
  R.head,
  R.propOr('', 'id')
)

export const ScrollAnchorHandler = (): JSX.Element => {
  const isMobile = useIsMobile()
  const dispatch = useDispatch()
  const { push, replace, location } = useHistory()
  const { search, pathname, state } = location
  const fromScroll = (state as any)?.fromScroll

  const userScrolled = React.useRef(false)

  React.useEffect(() => {
    const handleWheel = () => { userScrolled.current = true }
    window.addEventListener('wheel', handleWheel)
    window.addEventListener('touchmove', handleWheel)
    return () => {
      window.removeEventListener('wheel', handleWheel)
      window.removeEventListener('touchmove', handleWheel)
    }
  }, [])

  const anchorPositionY = useSelector(selectScrollAnchorPositionY)
  const bookContentsPositions = useSelector(selectBookContentsPositions)
  // @ts-ignore
  const bookContents: BookContent[] = useSelector(getOrderedBookContentsList)
  const bookContentsWithImages = useSelector(getBookContentsWithImages)
  const debug = LocalStorageService.get(LOCAL_STORAGE_KEYS.bookScrollDebug)
  const bookContentImages = useSelector(getBookContentImages)

  const TIMEOUT_VALUE = isMobile ? 0 : 700
  const DEBOUNCE_TIME = isMobile ? 0 : 200

  const getNearestBookContentImageIndexByContentId = bookContentId => {
    const bookContentIndex = R.findIndex(R.propEq('id', bookContentId))(
      bookContents
    )
    const findNearestImageId = R.pipe(
      R.take(bookContentIndex + 1),
      R.ifElse(
        hasCurrentBookContentImagesAttached,
        takeFirstImageId,
        findNearestImage
      )
    )(bookContentsWithImages)

    return R.findIndex(R.propEq('id', findNearestImageId))(bookContentImages)
  }

  const parsedQuery = qs.parse(search, { ignoreQueryPrefix: true })

  const params = useParams()
  const bookId: string = propOr('', 'bookId', params)
  const book = useSelector(state => getBookByOriginalId(state, bookId))
  const previewState = propOr('', 'preview_state', book)
  const rightColumnLocked = isNotNilOrEmpty(previewState)

  const saveScrollAnchorPosition = useCallback(
    positionY => dispatch(saveScrollAnchorPositionRoutine({ positionY })),
    [dispatch]
  )

  const saveScrollBookContentPositions = useCallback(
    positions => dispatch(saveBookContentPositionsRoutine(positions)),
    [dispatch]
  )

  React.useEffect(() => {
    const currentParsedQuery = qs.parse(search, { ignoreQueryPrefix: true })
    const sectionIdFromQuery: string = R.propOr(
      '',
      BOOK_VIEWER_PARAMS.sectionIdContext
    )(currentParsedQuery)
    const bookContentIdFromQuery: string = R.propOr(
      '',
      BOOK_VIEWER_PARAMS.bookContentIdContext
    )(currentParsedQuery)
    const isSectionOnlyNavigation =
      isNotNilOrEmpty(sectionIdFromQuery) &&
      !isNotNilOrEmpty(bookContentIdFromQuery)

    if (isSectionOnlyNavigation) {
      userScrolled.current = false
      saveScrollBookContentPositions([])
      return
    }

    if (!fromScroll) {
      userScrolled.current = false
    }
  }, [pathname, search, fromScroll, saveScrollBookContentPositions])

  const saveBookContentsPositions = () => {
    const positions = bookContents.map(bookContent => {
      const bookContentId: string = R.propOr('', 'id')(bookContent)
      const bookContentAnchor = document.getElementById(
        `book-content-anchor-${bookContentId}`
      )
      const dimensionsBookContentAnchor = bookContentAnchor
        ? bookContentAnchor.getBoundingClientRect()
        : {
            height: 0,
            top: 0
          }

      return {
        id: bookContentId,
        height: dimensionsBookContentAnchor.height,
        top: dimensionsBookContentAnchor.top
      }
    })
    saveScrollBookContentPositions(positions)
  }

  const saveAnchorPosition = () => {
    const scrollContainer = document.getElementById('left-container-scroller')
    // @ts-ignore
    const scrollContainerHeight = isMobile
      ? window.innerHeight
      : // @ts-ignore
        scrollContainer.clientHeight
    const dimensionsContainer = isMobile
      ? { y: 0 }
      : // @ts-ignore
        scrollContainer.getBoundingClientRect()

    // Minimum Y position of te anchor is the Y position of the scroll container
    const desktopContextAnchorMin = dimensionsContainer.y
    // Set the anchor checkpoint at 5/12 of the scroll container height
    const desktopContextAnchorMax =
      (scrollContainerHeight * 5) / 12 + desktopContextAnchorMin

    const mobileContextAnchorMax =
      scrollContainerHeight / 4 + desktopContextAnchorMin

    saveScrollAnchorPosition(
      isMobile ? mobileContextAnchorMax : desktopContextAnchorMax
    )
  }

  useEffect(() => {
    if (isNotNilOrEmpty(bookContents)) {
      if (isMobile) {
        saveBookContentsPositions()
      } else {
        setTimeout(saveBookContentsPositions, TIMEOUT_VALUE)
      }
    }
  }, [bookContents])

  const findCurrentContext = () => {
    const matchedBookContent = R.find(
      (bookContentPosition: { top: number; height: number; id: string }) => {
        const { top, height } = bookContentPosition
        const delta = top + height
        return delta > anchorPositionY && top <= anchorPositionY
      }
      // @ts-ignore
    )(bookContentsPositions)
    const matchedBookContentId = R.propOr('', 'id', matchedBookContent)

    const bookContent = R.find(R.propEq('id', matchedBookContentId))(
      bookContents
    )

    return bookContent
  }

  const handleScroll = () => saveBookContentsPositions()
  const debouncedScrollHandler = isMobile
    ? saveBookContentsPositions
    : debounce(handleScroll, DEBOUNCE_TIME)

  // somehow this push needs to be done with setTimeout...
  // I'm not sure why it is not working immediately
  const changePath = path => setTimeout(() => replace(path, { fromScroll: true }))

  React.useEffect(() => {
    saveAnchorPosition()

    window.addEventListener('resize', saveAnchorPosition)
    eventEmitter.on(events.updatePath, changePath)

    return () => {
      window.removeEventListener('resize', saveAnchorPosition)
      eventEmitter.off(events.updatePath, changePath)
    }
  }, [])

  React.useEffect(() => {
    const scrollContainer = isMobile
      ? window
      : document.getElementById('left-container-scroller')

    // @ts-ignore
    scrollContainer.addEventListener('scroll', debouncedScrollHandler)

    return () => {
      // @ts-ignore
      scrollContainer.removeEventListener('scroll', debouncedScrollHandler)
    }
  }, [bookContents])

  // Separate effect for initial load: update URL when bookContents are ready but no URL params exist
  // This ensures URL gets populated on server where positions might not be ready yet
  React.useEffect(() => {
    // Re-parse query to get latest URL state
    const currentParsedQuery = qs.parse(search, { ignoreQueryPrefix: true })
    const bookContentIdFromQuery: string = R.propOr(
      '',
      BOOK_VIEWER_PARAMS.bookContentIdContext
    )(currentParsedQuery)
    const sectionIdFromQuery: string = R.propOr(
      '',
      BOOK_VIEWER_PARAMS.sectionIdContext
    )(currentParsedQuery)
    const hasExplicitParams = isNotNilOrEmpty(bookContentIdFromQuery) || isNotNilOrEmpty(sectionIdFromQuery)
    
    // Only run on initial load when bookContents are available but no URL params exist
    if (!hasExplicitParams && isNotNilOrEmpty(bookContents) && bookContents.length > 0) {
      const firstBookContent = bookContents[0]
      const firstBookContentId: string = R.propOr('', 'id')(firstBookContent)
      const firstSubchapterId: string = R.propOr('', 'subchapter_id')(firstBookContent)
      
      if (isNotNilOrEmpty(firstBookContentId) && isNotNilOrEmpty(firstSubchapterId)) {
        const imageIndex = getNearestBookContentImageIndexByContentId(firstBookContentId)
        const newQuery = qs.stringify({
          ...currentParsedQuery,
          [BOOK_VIEWER_PARAMS.bookContentIdContext]: firstBookContentId,
          [BOOK_VIEWER_PARAMS.sectionIdContext]: firstSubchapterId,
          [BOOK_VIEWER_PARAMS.imageSlide]: imageIndex >= 0 ? imageIndex : '1'
        })
        
        eventEmitter.emit(events.updatePath, `${pathname}?${newQuery}`)
      }
    }
  }, [bookContents, pathname, search])

  React.useEffect(() => {
    const sectionIdFromQuery: string = R.propOr(
      '',
      BOOK_VIEWER_PARAMS.sectionIdContext
    )(parsedQuery)
    const bookContentIdFromQuery: string = R.propOr(
      '',
      BOOK_VIEWER_PARAMS.bookContentIdContext
    )(parsedQuery)
    const isSectionOnlyNavigation =
      isNotNilOrEmpty(sectionIdFromQuery) &&
      !isNotNilOrEmpty(bookContentIdFromQuery)

    if (isSectionOnlyNavigation) {
      return
    }

    const currentContext = findCurrentContext()
    const bookContentId: string = R.propOr('', 'id')(currentContext)
    const imageSlideFromQuery: string = R.propOr(
      '1',
      BOOK_VIEWER_PARAMS.imageSlide
    )(parsedQuery)
    const subchapterId: string = R.propOr('', 'subchapter_id')(currentContext)
    const imageIndex = getNearestBookContentImageIndexByContentId(bookContentId)

    const parsedPreviewState = qs.parse(previewState as string, {
      ignoreQueryPrefix: true
    })

    const shrinkBottomIfImageIsChanged =
      !rightColumnLocked && Number(imageIndex) !== Number(imageSlideFromQuery)
        ? {
            [BOOK_VIEWER_PARAMS.rightBottomExpand]: false,
            [BOOK_VIEWER_PARAMS.rightTopActive]: TABS.images
          }
        : {}

    const setDefaultValuesIfPadlockIsLocked = rightColumnLocked
      ? {
          [BOOK_VIEWER_PARAMS.rightBottomExpand]: propOr(
            false,
            BOOK_VIEWER_PARAMS.rightBottomExpand,
            parsedPreviewState
          ),
          [BOOK_VIEWER_PARAMS.rightTopActive]: propOr(
            false,
            BOOK_VIEWER_PARAMS.rightTopActive,
            parsedPreviewState
          ),
          [BOOK_VIEWER_PARAMS.rightBottomActive]: propOr(
            false,
            BOOK_VIEWER_PARAMS.rightBottomActive,
            parsedPreviewState
          ),
          [BOOK_VIEWER_PARAMS.rightTopExpand]: propOr(
            false,
            BOOK_VIEWER_PARAMS.rightTopExpand,
            parsedPreviewState
          )
        }
      : {}

    if (
      isNotNilOrEmpty(currentContext) &&
      bookContentId !== bookContentIdFromQuery &&
      (fromScroll || userScrolled.current || !bookContentIdFromQuery)
    ) {
      const newQuery = qs.stringify({
        ...parsedQuery,
        [BOOK_VIEWER_PARAMS.bookContentIdContext]: bookContentId,
        [BOOK_VIEWER_PARAMS.sectionIdContext]: subchapterId,
        [BOOK_VIEWER_PARAMS.imageSlide]:
          imageIndex >= 0 ? imageIndex : imageSlideFromQuery,
        ...shrinkBottomIfImageIsChanged,
        ...setDefaultValuesIfPadlockIsLocked
      })

      // I have no ide why simple push is not working here, so I made
      // this event emitter to handle push outside this useEffect
      eventEmitter.emit(events.updatePath, `${pathname}?${newQuery}`)
    }
  }, [bookContentsPositions, search, bookContents])

  return <Anchor positionY={anchorPositionY} debug={debug} />
}

export const ScrollAnchorHandlerWrapper = (): JSX.Element | null => {
  const isMobile = useIsMobile()
  const params = useParams()
  const bookId: string = R.propOr('', 'bookId')(params)
  const book = useSelector(state => getBookByOriginalId(state, bookId))

  const hasBook = isNotNilOrEmpty(book?.id)
  const scrollContainer = isMobile
    ? window
    : document.getElementById('left-container-scroller')

  if (!hasBook || !scrollContainer) {
    return null
  }

  return <ScrollAnchorHandler />
}

export default ScrollAnchorHandlerWrapper

const Anchor = styled.div`
  display: ${({ debug }) => (debug ? 'inline-block' : 'none')};
  position: fixed;
  width: 1280px;
  height: 5px;
  background: red;
  top: ${({ positionY }) => positionY}px;
  z-index: 5000;
`
